/**
 * CÓMO ME ENCUENTRAN PARA JUGAR.
 *
 * QUÉ PROBLEMA RESUELVE. El nivel que uno dice tener, si acepta retos, si los
 * avisos van también por correo y dónde está vivían adentro del formulario
 * del perfil, entre la cédula y la dirección. Javier los mandó acá, y tiene
 * razón por dos motivos:
 *
 *  1. NO SON QUIÉN ES UNO. El perfil es la cédula —nombre, documento, dónde
 *     vive—, y sobre eso nadie cambia de opinión. Sobre «¿quiero que me
 *     reten esta semana?» se cambia de opinión todo el tiempo.
 *  2. NO SE PODÍAN PISAR. Cada una se guarda sola, por su propia ruta, así
 *     que guardar el formulario del perfil con un valor viejo cargado ya no
 *     borra lo que se acaba de elegir acá.
 *
 * SE GUARDA AL TOCAR y no hay botón de guardar, igual que el tema: son cuatro
 * interruptores, y un formulario con botón obliga a acordarse de apretarlo
 * —y quien no lo hace cree que lo dejó puesto—.
 *
 * LOS TRES PRIMEROS VIAJAN JUNTOS aunque se toquen de a uno: se muestran
 * juntos, así que esta pantalla siempre tiene los tres en la mano, y tres
 * rutas serían tres viajes para lo que la persona vive como un solo ajuste.
 * La ubicación no: no se escribe, se MIDE, y ya tenía su propio camino.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CATEGORY_LEVELS, type CategoryLevel, type PlayerProfile } from '@tennisup/contracts';

import { Alert } from './Alert';
import { LocationBox } from './LocationBox';
import { Select } from './Select';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

/** El valor de «prefiero no decirlo»: un `<option>` no puede llevar `null`. */
const SIN_NIVEL = '';

export function PreferencesSection(): JSX.Element {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api
      .myProfile()
      .then((leído) => {
        if (vivo) setProfile(leído);
      })
      .catch((caught: unknown) => {
        if (vivo) setError(errorMessage(t, caught));
      });
    return () => {
      vivo = false;
    };
  }, [t]);

  /**
   * SE MANDAN LAS TRES, con lo que hay puesto ahora.
   *
   * `cambio` es lo que se acaba de tocar; el resto sale del perfil que esta
   * pantalla ya tiene. Mandar solo lo tocado obligaría al servidor a
   * distinguir «no lo mandé» de «lo apagué», que en un booleano es
   * exactamente la confusión que deja a alguien sin poder apagar nada.
   */
  async function guardar(cambio: {
    declaredLevel?: CategoryLevel | null;
    availableForChallenges?: boolean;
    hidden?: boolean;
    emailNotifications?: boolean;
  }): Promise<void> {
    if (profile === null) return;
    // OPTIMISTA: el interruptor se mueve en el acto. Un checkbox que tarda
    // medio segundo en marcarse se toca dos veces, y la segunda lo apaga.
    const antes = profile;
    const después = { ...profile, ...cambio };
    setProfile(después);
    setGuardando(true);
    setError(null);
    try {
      setProfile(
        await api.setPreferences({
          declaredLevel: después.declaredLevel,
          availableForChallenges: después.availableForChallenges,
          hidden: después.hidden,
          emailNotifications: después.emailNotifications,
        }),
      );
    } catch (caught) {
      // Y SI FALLA, VUELVE A COMO ESTABA. Dejarlo movido es peor que no
      // haberlo movido: la pantalla diría una cosa y la cuenta otra.
      setProfile(antes);
      setError(errorMessage(t, caught));
    } finally {
      setGuardando(false);
    }
  }

  if (error !== null && profile === null) return <Alert kind="error">{error}</Alert>;
  if (profile === null) return <p className="help">{t('common.loading')}</p>;

  return (
    <section className="prefs">
      <h2>{t('more.playing')}</h2>
      <p className="help">{t('more.playingHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/*
        EL NIVEL QUE UNO DICE TENER, no la categoría de ningún club. La de
        verdad la fija cada club y se cambia pidiéndosela; esto sirve para
        llegar preseleccionado al pedir entrar a uno, para que en Retos se
        sepa el nivel de alguien con quien no se comparte club, y para que
        quien todavía no está en ninguno pueda decir en qué juega.
      */}
      <div className="field">
        <label htmlFor="pref-level">{t('profile.declaredLevel')}</label>
        <span className="help">{t('profile.declaredLevelHelp')}</span>
        <Select
          id="pref-level"
          value={profile.declaredLevel ?? SIN_NIVEL}
          disabled={guardando}
          options={[
            { value: SIN_NIVEL, label: t('profile.noLevel') },
            ...CATEGORY_LEVELS.map((level) => ({
              value: level,
              label: t(`categoryLevel.${level}`),
            })),
          ]}
          onChange={(level) =>
            void guardar({ declaredLevel: level === SIN_NIVEL ? null : (level as CategoryLevel) })
          }
        />
      </div>

      <div className="field checkbox">
        <input
          id="pref-available"
          type="checkbox"
          checked={profile.availableForChallenges}
          disabled={guardando}
          onChange={(e) => void guardar({ availableForChallenges: e.target.checked })}
        />
        <div>
          <label htmlFor="pref-available">{t('register.available')}</label>
          <span className="help">{t('register.availableHelp')}</span>
        </div>
      </div>

      {/*
        DEBAJO DE LA DE RETOS Y NO EN OTRA SECCIÓN, aunque no sea del mismo
        tema: puestas una al lado de la otra se entiende que son distintas.
        «No me reten» deja el perfil abierto; «que no me encuentren» deja los
        retos como estén. Separadas, cualquiera prendería una creyendo que
        hace lo de la otra.

        LA AYUDA DICE QUÉ **NO** ESCONDE, y es la parte importante del texto:
        quien prende esto y después se ve a sí mismo en la tabla de su club
        cree que no funcionó. Decírselo antes evita ese reporte y, peor, que
        lo apague pensando que está roto.
      */}
      <div className="field checkbox">
        <input
          id="pref-hidden"
          type="checkbox"
          checked={profile.hidden}
          disabled={guardando}
          onChange={(e) => void guardar({ hidden: e.target.checked })}
        />
        <div>
          <label htmlFor="pref-hidden">{t('profile.hidden')}</label>
          <span className="help">{t('profile.hiddenHelp')}</span>
        </div>
      </div>

      {/* DEBAJO DE LA DE RETOS porque son las dos del mismo tema: cómo y por
          dónde nos comunicamos con esta persona. */}
      <div className="field checkbox">
        <input
          id="pref-emails"
          type="checkbox"
          checked={profile.emailNotifications}
          disabled={guardando}
          onChange={(e) => void guardar({ emailNotifications: e.target.checked })}
        />
        <div>
          <label htmlFor="pref-emails">{t('profile.emails')}</label>
          <span className="help">{t('profile.emailsHelp')}</span>
        </div>
      </div>

      {/* LA UBICACIÓN SE GUARDA SOLA, por su propio camino: no es un campo que
          se escriba, es una medición que se pide. Por eso se trajo tal cual y
          no hizo falta tocarla. */}
      <LocationBox profile={profile} onChanged={setProfile} />
    </section>
  );
}
